import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { PAGE, TYPE, trackingFor } from "../../theme/tokens";

// Shared building blocks for the landing page. These exist as a separate module
// because each is used by several sections and each carries a non-obvious
// detail (a universal font rule to defeat, a dash animation to seed, a duplicated
// track to make a marquee seamless) that shouldn't be re-derived per section.

/* ------------------------------------------------------------ typography -- */

// index.css carries `* { font-family: <body face> }` — a universal selector that
// outranks any family set on an ancestor. Every piece of text on this page must
// therefore declare its own face, which is what these three wrappers guarantee.

export const Display = ({ as: Tag = "h2", className = "", style, children, ...rest }) => (
  <Tag
    className={`font-display [text-wrap:balance] ${className}`}
    style={style}
    {...rest}
  >
    {children}
  </Tag>
);

export const Body = ({ as: Tag = "p", className = "", style, children, ...rest }) => (
  <Tag className={`font-body ${className}`} style={style} {...rest}>
    {children}
  </Tag>
);

// Tracking is derived from the size rather than fixed: 0.18em on 10px uppercase
// mono was the least legible combination on the page, so small sizes get tighter
// letterspacing automatically and callers can't reintroduce the problem. Size and
// tracking still go through `style` (they're computed, not one of the five fixed
// steps a Tailwind class could express) — only the font-family itself moved to a
// class.
export const Mono = ({ as: Tag = "span", className = "", style, children, ...rest }) => {
  const fontSize = style?.fontSize ?? TYPE.label;
  return (
    <Tag
      className={`font-mono uppercase ${className}`}
      style={{
        fontSize,
        letterSpacing: trackingFor(fontSize),
        ...style,
        // style may set fontSize; tracking must still follow it.
        ...(style?.fontSize ? { letterSpacing: style.letterSpacing ?? trackingFor(style.fontSize) } : null),
      }}
      {...rest}
    >
      {children}
    </Tag>
  );
};

/* ------------------------------------------------------------------ logo -- */

// The brand mark, everywhere it appears: the fancy "S" image mark, then
// "UBSCENE" set in text — the wordmark reads as one continuous "SUBSCENE"
// with the image standing in for the first letter, rather than an icon
// sitting beside an unrelated word. One component so every header/footer
// stays in sync rather than seven copies of the same markup drifting apart.
// `imgClassName` carries the mark's size, `textClassName` the text's.
export const Logo = ({ imgClassName = "h-7 w-7", textClassName = "text-[1.05rem]", className = "" }) => (
  <span className={`inline-flex items-center gap-x-[0.0625rem] ${className}`}>
    <img src="/logo/subscene-logo.png" alt="" className={`object-contain ${imgClassName}`} />
    <Display as="span" className={`font-bold tracking-[0.02em] text-bone ${textClassName}`}>
      UBSCENE
    </Display>
  </span>
);

/* --------------------------------------------------------------- surfaces -- */

// Sharp 1px grid, accent-biased rather than neutral grey.
//
// `color` defaults to var(--color-line) — a live CSS variable, not a static
// PAGE.line hex — so this texture is theme-aware wherever it's used inside
// .subscene-dashboard (dark by default; the dashboard's light theme
// overrides the variable, see src/theme/dashboardTheme.js) without callers
// needing to pass anything. The marketing landing page passes its own FILM
// line colour explicitly instead (a different token namespace, --t-line).
// Same default for DotTexture below.
export const GridTexture = ({ size = 64, opacity = 1, className = "", fade = true, color = "var(--color-line)" }) => (
  <div
    aria-hidden="true"
    className={`pointer-events-none absolute inset-0 ${className}`}
    style={{
      backgroundImage: `linear-gradient(${color} 1px, transparent 1px), linear-gradient(90deg, ${color} 1px, transparent 1px)`,
      backgroundSize: `${size}px ${size}px`,
      opacity,
      maskImage: fade
        ? "radial-gradient(120% 120% at 50% 0%, #000 25%, transparent 95%)"
        : undefined,
      WebkitMaskImage: fade
        ? "radial-gradient(120% 120% at 50% 0%, #000 25%, transparent 95%)"
        : undefined,
    }}
  />
);

export const DotTexture = ({ size = 22, opacity = 0.5, className = "", color = "var(--color-line)" }) => (
  <div
    aria-hidden="true"
    className={`pointer-events-none absolute inset-0 ${className}`}
    style={{
      backgroundImage: `radial-gradient(${color} 1.1px, transparent 1.1px)`,
      backgroundSize: `${size}px ${size}px`,
      opacity,
    }}
  />
);

// A hairline that draws itself when scrolled into view.
export const Hairline = ({ className = "", color = PAGE.line }) => (
  <motion.div
    aria-hidden="true"
    className={`hairline-drawn h-px origin-left ${className}`}
    style={{ "--hairline-color": color }}
    initial={{ scaleX: 0 }}
    whileInView={{ scaleX: 1 }}
    viewport={{ once: true }}
    transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
  />
);

/* ---------------------------------------------------------------- alerts -- */

/**
 * The error banner every tool and form was reimplementing.
 *
 * Seventeen copies across thirteen files, all hardcoding red-500/red-200 while
 * a `redText` token existed for exactly this. Beyond the duplication, the
 * hardcoded palette is invisible to the theme system: `redText` resolves
 * through a CSS variable and so follows the dashboard's light/dark switch,
 * where `text-red-200` does not.
 *
 * `tone="warning"` is the same shape in amber, for the "this worked but you
 * should know something" case that a few callers hand-rolled separately.
 */
export const ErrorBanner = ({
  children,
  tone = "error",
  size = "default",       // "default" | "compact" — compact is the inline form
  icon: Icon,
  className = "",
  ...rest
}) => {
  // The fill and border use `amber-500`/`red-500` while the TEXT keeps the
  // theme's own amberText/redText.
  //
  // That split is deliberate: amberText/redText are plain var() colours, so a
  // /NN modifier on them has nothing to inject alpha into and silently renders
  // SOLID — which meant every banner in the app was red text on a red block.
  // The stock Tailwind palette does take the modifier, and the theme tokens
  // stay where they work, on the text itself.
  const toneClasses =
    tone === "warning"
      ? "border-amber-500/50 bg-amber-500/10 text-amberText"
      : "border-red-500/50 bg-red-500/10 text-redText";

  const compact = size === "compact";
  const sizeClasses = compact
    ? "gap-2 rounded-lg p-3 text-xs"
    : "gap-3 rounded-xl p-4 text-sm";
  const iconSize = compact ? "h-3.5 w-3.5" : "h-5 w-5";

  return (
    <div
      role={tone === "error" ? "alert" : "status"}
      className={`flex items-start border font-body ${sizeClasses} ${toneClasses} ${className}`}
      {...rest}
    >
      {Icon && <Icon className={`mt-0.5 flex-shrink-0 ${iconSize}`} aria-hidden="true" />}
      <div className="min-w-0">{children}</div>
    </div>
  );
};


/* ----------------------------------------------------------------- label -- */

// `accent` defaults to var(--color-volt) — used across dashboard pages
// (ScreenWriter, SpeechGenerator, StockWorkflows, Home) as well as the
// landing page, so it needs to track whichever palette is actually active
// rather than always being the dark dashboard's volt green.
export const Eyebrow = ({ children, accent = "var(--color-volt)", marker }) => (
  <div className="relative mb-5 flex items-center gap-3">
    <span
      aria-hidden="true"
      className="eyebrow-dot inline-block h-[7px] w-[7px] rounded-full"
      style={{ "--eyebrow-accent": accent }}
    />
    <Mono className="text-label font-medium" style={{ color: accent }}>{children}</Mono>
    {marker && (
      <>
        <span aria-hidden="true" className="h-px flex-1 bg-line" />
        {/* eyebrow-marker: DashboardStyles hides this below md, where the
            mobile nav trigger floats at top-right and would clip it. Scoped to
            .subscene-dashboard there rather than hidden here, because this
            component is shared with the landing page, which has no such
            trigger and keeps its marker at every width. */}
        <Mono className="eyebrow-marker text-label text-mute">{marker}</Mono>
      </>
    )}
  </div>
);

/* ------------------------------------------------------------------- art -- */

// Generated artwork is rendered on the page's own ground colour, but matching
// the hex is not enough on its own: a rectangle of image against a flat field
// still shows its edges wherever the render is even slightly lighter. Feathering
// the boundary with a radial mask is what actually makes the art sit *in* the
// page rather than on top of it.
export const FeatheredArt = ({ src, alt, className = "", style, feather = 62 }) => (
  <img
    src={src}
    alt={alt}
    loading="lazy"
    className={className}
    style={{
      maskImage: `radial-gradient(115% 105% at 50% 50%, #000 ${feather}%, transparent 97%)`,
      WebkitMaskImage: `radial-gradient(115% 105% at 50% 50%, #000 ${feather}%, transparent 97%)`,
      ...style,
    }}
  />
);

/* --------------------------------------------------------- labelled art -- */

// Artwork plus its labels, with the labels living in the DOM rather than baked
// into the render. Generated text is inconsistent between images — different
// faces, different weights, wrong sizes once scaled, and untranslatable — so the
// images are produced with no lettering at all and the labels are positioned
// over them here, in the page's own typeface.
//
// `labels` is a list of { text, x, y } where x/y are percentages of the frame.
export const LabelledArt = ({
  src,
  alt,
  labels = [],
  className = "",
  style,
  feather = 62,
}) => (
  <div className={`relative ${className}`} style={style}>
    <FeatheredArt src={src} alt={alt} className="w-full" feather={feather} />
    {labels.map((l) => (
      <div
        key={l.text}
        className="pointer-events-none absolute hidden -translate-x-1/2 -translate-y-1/2 sm:block"
        style={{ left: `${l.x}%`, top: `${l.y}%` }}
      >
        <Mono className="art-label">
          {l.text}
        </Mono>
      </div>
    ))}
  </div>
);

/* -------------------------------------------------------------- pipeline -- */

// A marching dashed line with an optional travelling packet, in the page's
// accent. Used to wire sections together so the page reads as a flow rather
// than a stack of blocks. The dash march is CSS (cheap, compositor-friendly);
// the draw-on is Framer so it can be triggered by scroll.
export const Pipeline = ({
  d,
  viewBox = "0 0 100 100",
  className = "",
  color = PAGE.volt,
  width = 1.5,
  speed = 1.1,
  packets = 0,
  reduced = false,
  preserveAspectRatio = "none",
  drawOnScroll = true,
}) => (
  <svg
    aria-hidden="true"
    viewBox={viewBox}
    fill="none"
    preserveAspectRatio={preserveAspectRatio}
    className={`pointer-events-none ${className}`}
  >
    <motion.path
      d={d}
      stroke={color}
      strokeWidth={width}
      strokeLinecap="round"
      strokeDasharray="4 6"
      vectorEffect="non-scaling-stroke"
      initial={reduced || !drawOnScroll ? { pathLength: 1, opacity: 0.55 } : { pathLength: 0, opacity: 0 }}
      whileInView={{ pathLength: 1, opacity: 0.55 }}
      viewport={{ once: true, margin: "0px 0px 100px 0px" }}
      transition={{ duration: reduced ? 0 : 1.1, ease: [0.16, 1, 0.3, 1] }}
      style={reduced ? undefined : { animation: `subsceneDash ${speed}s linear infinite` }}
    />
    {/* Packets travel the same path, so the line reads as carrying something. */}
    {!reduced &&
      Array.from({ length: packets }).map((_, i) => (
        <circle key={i} r={width * 1.6} fill={color}>
          <animateMotion
            dur={`${speed * 3.4}s`}
            repeatCount="indefinite"
            begin={`${(i * speed * 3.4) / Math.max(packets, 1)}s`}
            path={d}
          />
        </circle>
      ))}
  </svg>
);

/* ---------------------------------------------------------- dotted arrow -- */

// A marching dotted line that draws on scroll, then keeps its dashes moving so
// it reads as flow rather than decoration. `d` is any SVG path; the dash offset
// animation is what turns a static stroke into a direction.
export const DottedArrow = ({
  d,
  className = "",
  viewBox = "0 0 100 100",
  color = PAGE.volt,
  delay = 0,
  reduced = false,
  showHead = true,
  headAt = { x: 50, y: 95, rotate: 0 },
}) => (
  <svg
    aria-hidden="true"
    viewBox={viewBox}
    fill="none"
    preserveAspectRatio="none"
    className={`pointer-events-none absolute ${className}`}
  >
    <motion.path
      d={d}
      stroke={color}
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeDasharray="5 7"
      vectorEffect="non-scaling-stroke"
      initial={reduced ? { pathLength: 1, opacity: 0.65 } : { pathLength: 0, opacity: 0 }}
      whileInView={{ pathLength: 1, opacity: 0.65 }}
      viewport={{ once: true, margin: "0px 0px 100px 0px" }}
      transition={{ duration: reduced ? 0 : 1.1, delay: reduced ? 0 : delay, ease: [0.16, 1, 0.3, 1] }}
      style={
        reduced
          ? undefined
          : { animation: `subsceneDash 1.1s linear infinite`, animationDelay: `${delay}s` }
      }
    />
    {showHead && (
      <motion.path
        d="M -4 -4 L 0 0 L -4 4"
        stroke={color}
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
        transform={`translate(${headAt.x} ${headAt.y}) rotate(${headAt.rotate})`}
        initial={reduced ? { opacity: 0.85 } : { opacity: 0 }}
        whileInView={{ opacity: 0.85 }}
        viewport={{ once: true, margin: "0px 0px 100px 0px" }}
        transition={{ duration: 0.3, delay: reduced ? 0 : delay + 0.9 }}
      />
    )}
  </svg>
);

/* --------------------------------------------------------------- marquee -- */

// The track is rendered twice and translated by exactly -50%, which is what
// makes the loop seamless — any other distance shows a seam at the wrap.
export const Marquee = ({ items, speed = 38, reduced = false, separator = "/" }) => {
  const track = [...items, ...items];
  return (
    <div className="marquee-mask group relative flex overflow-hidden">
      {/* The animation is driven by a class rather than an inline style: an
          inline `animation-play-state` would outrank the stylesheet's
          hover-to-pause rule, since inline styles beat any selector. */}
      <div
        className={`flex shrink-0 items-center gap-8 whitespace-nowrap py-4 pr-8 ${
          reduced ? "" : "subscene-marquee-track"
        }`}
        style={reduced ? undefined : { animationDuration: `${speed}s` }}
      >
        {track.map((item, i) => (
          <span key={`${item}-${i}`} className="flex items-center gap-8">
            <Mono className="text-label text-mute">{item}</Mono>
            {/* Separator is ornament, so it takes a dim token rather than alpha
                on a live colour — same reason as the text tokens above. */}
            <span aria-hidden="true" className="text-muteDim">
              {separator}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
};

/* -------------------------------------------------- scroll counter helper -- */

// Counts up to `to` once the element enters view. Used for the mix figures.
export const CountUp = ({ to, duration = 1200, reduced = false, className = "", style }) => {
  const ref = useRef(null);
  // Positive margin, not negative: see the note on viewportOnce in
  // utils/motion.js — a shrunk trigger band can be skipped in one fast
  // scroll step, leaving this counter permanently stuck at 0.
  const inView = useInView(ref, { once: true, margin: "0px 0px 100px 0px" });
  const [value, setValue] = useState(reduced ? to : 0);

  useEffect(() => {
    if (!inView || reduced) {
      if (reduced) setValue(to);
      return;
    }
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      // expo-out, matching the page's easing vocabulary
      const eased = 1 - Math.pow(2, -10 * t);
      setValue(Math.round(to * (t === 1 ? 1 : eased)));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration, reduced]);

  return (
    <span ref={ref} className={`tabular-nums ${className}`} style={style}>
      {value}
    </span>
  );
};

/* ------------------------------------------------------------- dropdown -- */

// A styled stand-in for <select>/<option>. Native option popups pull their
// background and selection highlight from the OS rather than CSS — Chrome and
// Safari both ignore `background-color`/`color` on <option>, so a dark theme
// can style the closed control but not the open popup. This renders its own
// panel instead, matching the page everywhere the dropdown can appear.
export const Dropdown = ({
  value,
  onChange,
  options, // [{ value, label }] or plain strings
  disabled = false,
  placeholder = "Select…",
  className = "",
  id,
  size = "default", // "default" | "sm" — sm is a compact trigger for tight filter rows
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const items = options.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  const current = items.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const triggerSizeClass = size === "sm"
    ? "gap-1.5 rounded-md px-2 py-1.5 text-xs"
    : "gap-2 rounded-xl px-4 py-3";

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        id={id}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`flex w-full items-center justify-between border border-line bg-panelHi text-left font-body text-bone transition-colors focus:outline-none focus:ring-2 focus:ring-volt disabled:opacity-50 ${triggerSizeClass}`}
      >
        <span className={`truncate ${current ? "" : "text-muteDim"}`}>{current ? current.label : placeholder}</span>
        <svg
          aria-hidden="true"
          viewBox="0 0 12 8"
          className={`h-2.5 w-3 flex-shrink-0 text-mute transition-transform duration-150 ${open ? "rotate-180" : ""}`}
        >
          <path d="M1 1l5 5 5-5" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <ul
          role="listbox"
          className={`absolute top-[calc(100%+6px)] z-30 max-h-60 overflow-y-auto rounded-xl border border-line bg-inkRaised p-1.5 shadow-[0_20px_50px_rgba(0,0,0,0.5)] ${
            size === "sm" ? "left-0 w-max min-w-full max-w-[220px]" : "left-0 right-0"
          }`}
        >
          {items.map((item) => {
            const selected = item.value === value;
            return (
              <li key={item.value} role="option" aria-selected={selected}>
                <button
                  type="button"
                  onClick={() => {
                    onChange(item.value);
                    setOpen(false);
                  }}
                  className={`block w-full whitespace-nowrap rounded-lg text-left font-body transition-colors ${
                    size === "sm" ? "px-2.5 py-1.5 text-xs" : "px-3 py-2 text-sm"
                  } ${selected ? "bg-volt/15 text-volt" : "text-bone hover:bg-panelHi"}`}
                >
                  {item.label}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

/* --------------------------------------------------------------- slider -- */

// The one range-input treatment for every parameter slider on the dashboard
// (script length, narration temperature, speech stability/similarity/speed).
// Track color is bg-mute deliberately, not bg-panelHi/bg-white: mute is the
// one token that's already a legible dark tone in light theme and a legible
// light tone in dark theme, so the track never washes out against its own
// panel background — see the "sliders in light theme" fix this replaces.
export const Slider = ({
  id,
  label,
  icon: Icon,
  value,
  onChange,
  min,
  max,
  step,
  formatValue = (v) => v,
  minLabel,
  maxLabel,
  disabled = false,
  className = "",
}) => (
  <div className={className}>
    <div className="mb-2 flex items-baseline justify-between">
      <label htmlFor={id} className="flex items-center gap-1.5 font-body text-sm font-medium text-bone">
        {Icon && <Icon className="h-3.5 w-3.5 text-muteDim" />}
        {label}
      </label>
      <span className="rounded-full bg-panelHi px-2.5 py-1 font-mono text-xs normal-case tabular-nums text-volt">
        {formatValue(value)}
      </span>
    </div>
    <input
      id={id}
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      disabled={disabled}
      className="h-0.5 w-full cursor-pointer appearance-none rounded-lg bg-mute accent-volt disabled:opacity-50"
    />
    {(minLabel || maxLabel) && (
      <div className="mt-1 flex justify-between text-[11px] text-muteDim">
        <span>{minLabel}</span>
        <span>{maxLabel}</span>
      </div>
    )}
  </div>
);
